import React, { useEffect, useRef, useState } from 'react';
import { useTabs } from '../context/TabsContext';
import TimerIcon from './ui/TimerIcon';

const FloatingAlarmsTab = ({ onClick, timerAlarmActive = false, activeTimersCount = 0, timeRemaining = 0, formatTime, alarmEnabled = false, isModalOpen = false, onClose }) => {
    const { areTabsCollapsed, collapseTabs, expandTabs } = useTabs();
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState(() => {
        try {
            const saved = localStorage.getItem('alarmsTabPosition');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed === 'object' && 
                    (parsed.edge === 'left' || parsed.edge === 'bottom') &&
                    typeof parsed.offset === 'number' && 
                    parsed.offset >= 0) {
                    
                    if (typeof window !== 'undefined') {
                        const viewportWidth = window.innerWidth;
                        const viewportHeight = window.innerHeight;
                        const buttonSize = 88;
                        
                        if (parsed.edge === 'left') {
                            const maxOffset = Math.max(24, viewportHeight - buttonSize - 24);
                            return {
                                edge: parsed.edge,
                                offset: Math.max(24, Math.min(parsed.offset, maxOffset))
                            };
                        } else {
                            const maxOffset = Math.max(24, viewportWidth - buttonSize - 24);
                            return {
                                edge: parsed.edge,
                                offset: Math.max(24, Math.min(parsed.offset, maxOffset))
                            };
                        }
                    }
                    return parsed;
                }
            }
        } catch (e) {
            console.warn('Failed to parse alarmsTabPosition from localStorage:', e);
        }
        return { edge: 'left', offset: typeof window !== 'undefined' ? Math.max(24, window.innerHeight / 2 + 50) : 24 };
    });
    
    // Use refs to avoid stale closures
    const positionRef = useRef(position);
    const dragStartRef = useRef(null);
    const buttonRef = useRef(null);
    const isDraggingRef = useRef(false);
    const isModalOpenRef = useRef(isModalOpen);
    const onCloseRef = useRef(onClose);
    const onClickRef = useRef(onClick);
    
    // Keep refs in sync with state and props
    useEffect(() => {
        positionRef.current = position;
    }, [position]);
    
    useEffect(() => {
        isDraggingRef.current = isDragging;
    }, [isDragging]);
    
    useEffect(() => {
        isModalOpenRef.current = isModalOpen;
    }, [isModalOpen]);
    
    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);
    
    useEffect(() => {
        onClickRef.current = onClick;
    }, [onClick]);

    // Save position to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('alarmsTabPosition', JSON.stringify(position));
    }, [position]);

    // Validate and clamp position when window resizes
    useEffect(() => {
        const validatePosition = () => {
            if (typeof window === 'undefined') return;
            
            setPosition(currentPosition => {
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const buttonSize = 88;
                
                let needsUpdate = false;
                let newPosition = { ...currentPosition };
                
                if (currentPosition.edge === 'left') {
                    const maxOffset = Math.max(24, viewportHeight - buttonSize - 24);
                    if (currentPosition.offset > maxOffset) {
                        newPosition.offset = maxOffset;
                        needsUpdate = true;
                    }
                } else {
                    const maxOffset = Math.max(24, viewportWidth - buttonSize - 24);
                    if (currentPosition.offset > maxOffset) {
                        newPosition.offset = maxOffset;
                        needsUpdate = true;
                    }
                }
                
                return needsUpdate ? newPosition : currentPosition;
            });
        };
        
        validatePosition();
        window.addEventListener('resize', validatePosition);
        
        return () => {
            window.removeEventListener('resize', validatePosition);
        };
    }, []);

    const getDynamicStyle = () => {
        const bodyPadding = 'var(--spacing-sm)';
        const buttonSize = areTabsCollapsed ? 10 : 88; // Use sliver size when collapsed
        
        let safeOffset = position.offset;
        if (typeof window !== 'undefined') {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            
            if (position.edge === 'left') {
                const maxOffset = Math.max(24, viewportHeight - (areTabsCollapsed ? 10 : 88) - 24);
                safeOffset = Math.max(24, Math.min(position.offset, maxOffset));
            } else {
                const maxOffset = Math.max(24, viewportWidth - (areTabsCollapsed ? 10 : 88) - 24);
                safeOffset = Math.max(24, Math.min(position.offset, maxOffset));
            }
        }
        
        if (position.edge === 'left') {
            return {
                left: areTabsCollapsed ? '0px' : `calc(0px - ${bodyPadding})`,
                top: `${safeOffset}px`,
                position: 'fixed',
                margin: '0',
                transform: 'translateY(0)',
                zIndex: '9999',
                cursor: isDragging ? 'grabbing' : 'grab'
            };
        } else {
            return {
                bottom: areTabsCollapsed ? '0px' : `calc(0px - ${bodyPadding})`,
                left: `${safeOffset}px`,
                position: 'fixed',
                margin: '0',
                transform: 'translateY(0)',
                zIndex: '9999',
                cursor: isDragging ? 'grabbing' : 'grab'
            };
        }
    };

    const handleMouseDown = (e) => {
        if (e.button !== 0) return;
        
        // Reset dragging state and set up drag start
        setIsDragging(false);
        isDraggingRef.current = false;
        dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            startPos: { ...positionRef.current },
            startOffset: positionRef.current.offset,
            hasMoved: false
        };
        e.preventDefault();
    };

    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        
        // Reset dragging state and set up drag start
        setIsDragging(false);
        isDraggingRef.current = false;
        dragStartRef.current = {
            x: touch.clientX,
            y: touch.clientY,
            startPos: { ...positionRef.current },
            startOffset: positionRef.current.offset,
            hasMoved: false
        };
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!dragStartRef.current) return;
        
        const deltaX = e.clientX - dragStartRef.current.x;
        const deltaY = e.clientY - dragStartRef.current.y;
        const moveThreshold = 10;
        const collapseThreshold = 30; // Distance to trigger collapse
        
        // Check if dragging towards edge (inward) to collapse tabs
        const startPos = dragStartRef.current.startPos;
        if (startPos.edge === 'left' && deltaX < -collapseThreshold) {
            // Dragging left (towards edge) - collapse tabs
            collapseTabs();
        } else if (startPos.edge === 'bottom' && deltaY > collapseThreshold) {
            // Dragging down (towards edge) - collapse tabs
            collapseTabs();
        }
        
        // If moved beyond threshold, mark as moved and start dragging
        if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
            if (dragStartRef.current) {
                dragStartRef.current.hasMoved = true;
                
                if (!isDraggingRef.current) {
                    setIsDragging(true);
                    isDraggingRef.current = true;
                }
                
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const buttonSize = 88;
                const margin = 24;
                
                let newPosition = { ...startPos };
                
                // Determine which edge is closer
                const distToLeft = Math.abs(e.clientX);
                const distToBottom = Math.abs(viewportHeight - e.clientY);
                
                if (distToLeft < distToBottom) {
                    newPosition.edge = 'left';
                    // Fix: use +deltaY (when dragging up, deltaY is negative, so adding it decreases offset)
                    newPosition.offset = Math.max(margin, Math.min(viewportHeight - buttonSize - margin, dragStartRef.current.startOffset + deltaY));
                } else {
                    newPosition.edge = 'bottom';
                    newPosition.offset = Math.max(margin, Math.min(viewportWidth - buttonSize - margin, dragStartRef.current.startOffset + deltaX));
                }
                
                setPosition(newPosition);
            }
        }
    };

    const handleTouchMove = (e) => {
        if (!dragStartRef.current) return;
        
        const touch = e.touches[0];
        const deltaX = touch.clientX - dragStartRef.current.x;
        const deltaY = touch.clientY - dragStartRef.current.y;
        const moveThreshold = 10;
        const collapseThreshold = 30; // Distance to trigger collapse
        
        // Check if dragging towards edge (inward) to collapse tabs
        const startPos = dragStartRef.current.startPos;
        if (startPos.edge === 'left' && deltaX < -collapseThreshold) {
            // Dragging left (towards edge) - collapse tabs
            collapseTabs();
        } else if (startPos.edge === 'bottom' && deltaY > collapseThreshold) {
            // Dragging down (towards edge) - collapse tabs
            collapseTabs();
        }
        
        // If moved beyond threshold, mark as moved and start dragging
        if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
            if (dragStartRef.current) {
                dragStartRef.current.hasMoved = true;
                
                if (!isDraggingRef.current) {
                    setIsDragging(true);
                    isDraggingRef.current = true;
                }
                
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const buttonSize = 88;
                const margin = 24;
                
                let newPosition = { ...startPos };
                
                // Determine which edge is closer
                const distToLeft = Math.abs(touch.clientX);
                const distToBottom = Math.abs(viewportHeight - touch.clientY);
                
                if (distToLeft < distToBottom) {
                    newPosition.edge = 'left';
                    // Fix: use +deltaY (when dragging up, deltaY is negative, so adding it decreases offset)
                    newPosition.offset = Math.max(margin, Math.min(viewportHeight - buttonSize - margin, dragStartRef.current.startOffset + deltaY));
                } else {
                    newPosition.edge = 'bottom';
                    newPosition.offset = Math.max(margin, Math.min(viewportWidth - buttonSize - margin, dragStartRef.current.startOffset + deltaX));
                }
                
                setPosition(newPosition);
            }
        }
    };

    const handleMouseUp = (e) => {
        // Check if this was a click (no drag) BEFORE resetting state
        let shouldClick = false;
        if (dragStartRef.current) {
            const hasMoved = dragStartRef.current.hasMoved || false;
            const deltaX = e.clientX - dragStartRef.current.x;
            const deltaY = e.clientY - dragStartRef.current.y;
            const moveThreshold = 10;
            
            // Only trigger click if we didn't drag
            // Be more lenient when modal is open (for closing with X)
            const isModalOpen = isModalOpenRef.current;
            if (isModalOpen) {
                // When modal is open, allow clicks with slightly more movement tolerance
                shouldClick = !hasMoved && Math.abs(deltaX) <= moveThreshold * 1.5 && Math.abs(deltaY) <= moveThreshold * 1.5;
            } else {
                shouldClick = !hasMoved && Math.abs(deltaX) <= moveThreshold && Math.abs(deltaY) <= moveThreshold;
            }
        }
        
        // Always reset state immediately
        setIsDragging(false);
        isDraggingRef.current = false;
        const dragStart = dragStartRef.current;
        dragStartRef.current = null;
        
        // Handle click after state is cleared
        if (shouldClick) {
            // If tabs are collapsed (showing only sliver), always expand them first
            // Do NOT open modals when tabs are in stored/collapsed state
            if (areTabsCollapsed) {
                expandTabs();
                return; // Prevent any modal from opening
            }
            
            // Only open modals/close modals when tabs are expanded
            if (isModalOpenRef.current && onCloseRef.current) {
                onCloseRef.current();
            } else if (onClickRef.current) {
                onClickRef.current(e);
            }
        }
    };

    const handleTouchEnd = (e) => {
        // Check if this was a click (no drag) BEFORE resetting state
        let shouldClick = false;
        if (dragStartRef.current && e.changedTouches[0]) {
            const hasMoved = dragStartRef.current.hasMoved || false;
            const touch = e.changedTouches[0];
            const deltaX = touch.clientX - dragStartRef.current.x;
            const deltaY = touch.clientY - dragStartRef.current.y;
            const moveThreshold = 10;
            
            // Only trigger click if we didn't drag
            // Be more lenient when modal is open (for closing with X)
            const isModalOpen = isModalOpenRef.current;
            if (isModalOpen) {
                // When modal is open, allow clicks with slightly more movement tolerance
                shouldClick = !hasMoved && Math.abs(deltaX) <= moveThreshold * 1.5 && Math.abs(deltaY) <= moveThreshold * 1.5;
            } else {
                shouldClick = !hasMoved && Math.abs(deltaX) <= moveThreshold && Math.abs(deltaY) <= moveThreshold;
            }
        }
        
        // Always reset state immediately
        setIsDragging(false);
        isDraggingRef.current = false;
        dragStartRef.current = null;
        
        // Handle click after state is cleared
        if (shouldClick) {
            // If tabs are collapsed (showing only sliver), always expand them first
            // Do NOT open modals when tabs are in stored/collapsed state
            if (areTabsCollapsed) {
                expandTabs();
                return; // Prevent any modal from opening
            }
            
            // Only open modals/close modals when tabs are expanded
            if (isModalOpenRef.current && onCloseRef.current) {
                onCloseRef.current();
            } else if (onClickRef.current) {
                onClickRef.current(e);
            }
        }
    };

    // Set up event listeners - use refs to avoid stale closures
    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, []); // Empty dependency array - handlers use refs

    return (
        <div
            ref={buttonRef}
            className="fixed z-[9999]"
            style={getDynamicStyle()}
        >
            <button
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                className={position.edge === 'left' 
                    ? `rounded-r-lg ${areTabsCollapsed ? 'w-[10px] h-16' : 'w-20 h-16'}` 
                    : `rounded-t-lg ${areTabsCollapsed ? 'w-16 h-[10px]' : 'w-16 h-20'}`
                }
                style={{
                    margin: '0 !important',
                    padding: '0 !important',
                    backgroundColor: timerAlarmActive ? '#dc2626' : '#FFFFFF',
                    borderTop: '1px solid #0D0D0D',
                    borderRight: position.edge === 'left' ? '1px solid #0D0D0D' : '1px solid #0D0D0D',
                    borderLeft: position.edge === 'left' ? 'none' : '1px solid #0D0D0D',
                    borderBottom: position.edge === 'left' ? '1px solid #0D0D0D' : 'none',
                    boxShadow: position.edge === 'left' ? '2px 0 2px rgba(0, 0, 0, 0.02)' : '0 -1px 2px rgba(0, 0, 0, 0.02)',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    opacity: 1
                }}
            >
                {!areTabsCollapsed && (
                    isModalOpen ? (
                        <span className="text-neutral-900 font-mono text-lg">✕</span>
                    ) : timerAlarmActive ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C11.17 11.5 10.5 10.83 10.5 10C10.5 9.17 11.17 8.5 12 8.5C12.83 8.5 13.5 9.17 13.5 10C13.5 10.83 12.83 11.5 12 11.5Z" fill="#FFFFFF"/>
                        </svg>
                    ) : (
                        <TimerIcon size={20} className="text-neutral-900" />
                    )
                )}
            </button>
        </div>
    );
};

export default FloatingAlarmsTab;
